import { establishSessionWithinLock } from './login';
import {
  LoginOutcome,
  MFA_PATHS,
  MfaCallOptions,
  MfaChallenge,
  MfaSetupDetails,
  readChallenge,
  throwIfAborted,
  UnexpectedMfaResponseError,
} from './mfaChallenge';
import { withSessionLock } from './sessionLock';
import { postWebSession } from './webSession';

/**
 * YAC-2026-009 PR5a。2段階認証の途中の段（トークンを出す前）。202 の次のチャレンジを返す。
 * Cookie は書き換えないが、ログインと同じ錠の中で送る（別のタブのログイン・更新と入れ違わない）。
 */
function postMfaStep(
  path: string,
  body: Record<string, string>,
  { signal }: MfaCallOptions = {}
): Promise<MfaChallenge> {
  return withSessionLock(async () => {
    throwIfAborted(signal); // 錠を待つ間にやめた試行は送らない
    const response = await postWebSession(path, body);
    throwIfAborted(signal);
    return readChallenge(response);
  });
}

/** 初めてのログインの、初期パスワードの変更（mfa が password_change_required）。 */
export function mfaChangeInitialPassword(
  token: string,
  password: string,
  options?: MfaCallOptions
): Promise<MfaChallenge> {
  return postMfaStep(MFA_PATHS.initialPassword, { mfa_token: token, password }, options);
}

/** メールで届いたコード（mfa が email_code_required）。 */
export function mfaVerifyEmailCode(token: string, code: string, options?: MfaCallOptions): Promise<MfaChallenge> {
  return postMfaStep(MFA_PATHS.emailCode, { mfa_token: token, code }, options);
}

/** メールのコードの送り直し。mfa_token は替わらない（返った値を使う）。 */
export function mfaResendEmailCode(token: string, options?: MfaCallOptions): Promise<MfaChallenge> {
  return postMfaStep(MFA_PATHS.emailResend, { mfa_token: token }, options);
}

/** 認証アプリの登録に使う値（mfa が setup_required）。QR は data:image/svg+xml だけを受け付ける。 */
export function mfaSetupDetails(token: string, { signal }: MfaCallOptions = {}): Promise<MfaSetupDetails> {
  return withSessionLock(async () => {
    throwIfAborted(signal);
    const data = (await postWebSession(MFA_PATHS.setup, { mfa_token: token }))?.data;
    throwIfAborted(signal);
    const { secret, otpauth_uri: otpauthUri, qr_svg: qrSvg } = data ?? {};
    if (
      typeof secret !== 'string' ||
      typeof otpauthUri !== 'string' ||
      typeof qrSvg !== 'string' ||
      !qrSvg.startsWith('data:image/svg+xml')
    ) {
      throw new UnexpectedMfaResponseError();
    }
    const expiresIn = typeof data.expires_in === 'number' ? data.expires_in : 0;
    return { secret, otpauthUri, qrSvg, expiresIn };
  });
}

/** 2段階認証の最後の段（200 {access}・Cookie）。ログインと同じ確かめをしてから結果を返す。 */
function finishMfa(
  path: string,
  body: Record<string, string>,
  { signal }: MfaCallOptions = {}
): Promise<LoginOutcome> {
  return withSessionLock(async () => {
    throwIfAborted(signal);
    // EV-491。離れた画面の更新の応答は、前のセッションの Path の Cookie しか書けない（待たない）。
    // 要求そのものは取り消さない（サーバーが処理済みなら Cookie が発行されているので、応答の access の sid で
    // 失効させる必要がある）。やめた試行の結果は establishSessionWithinLock が捨てる。
    const access = (await postWebSession(path, body))?.data?.access;
    if (!access) {
      throwIfAborted(signal);
      throw new UnexpectedMfaResponseError();
    }
    return establishSessionWithinLock(access, signal);
  });
}

/** 認証アプリの最初のコードで登録を確定し、ログインを終える。 */
export function mfaConfirmSetup(token: string, code: string, options?: MfaCallOptions): Promise<LoginOutcome> {
  return finishMfa(MFA_PATHS.setupConfirm, { mfa_token: token, code }, options);
}

/** 認証アプリのコードで、ログインを終える（mfa が totp_required）。 */
export function mfaVerifyTotp(token: string, code: string, options?: MfaCallOptions): Promise<LoginOutcome> {
  return finishMfa(MFA_PATHS.totp, { mfa_token: token, code }, options);
}
